import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse } from '@/types';
import type { ToolRunResult } from '@/services/toolDefinition.service';
import type {
	CustomTool,
	CustomToolPayload,
	CustomToolSuggestion,
	CustomToolSuggestionRequest,
	CustomToolTestRequest,
	CustomToolUpdate,
	SlugCheck
} from '@/types/custom-tool.types';

type BuScope = { bu_id?: string };

/**
 * Custom tools are per account. The brand matters only where the backend works with the brand's
 * tools, so only the writes, the suggestion and the test run carry it.
 */
export const customToolService = {
	async list(): Promise<CustomTool[]> {
		const response = await api.get<ApiResponse<{ tools: CustomTool[] }>>(API_ENDPOINTS.CUSTOM_TOOLS.BASE);
		return response.data.data.tools;
	},

	async get(id: string): Promise<CustomTool> {
		const response = await api.get<ApiResponse<{ tool: CustomTool }>>(API_ENDPOINTS.CUSTOM_TOOLS.BY_ID(id));
		return response.data.data.tool;
	},

	async checkSlug(slug: string): Promise<SlugCheck> {
		const response = await api.get<ApiResponse<SlugCheck>>(API_ENDPOINTS.CUSTOM_TOOLS.SLUG_CHECK, { params: { slug } });
		return response.data.data;
	},

	/** A name, a free slug and a description for the steps and inputs so far. */
	async suggest(request: CustomToolSuggestionRequest, scope: BuScope = {}): Promise<CustomToolSuggestion> {
		const response = await api.post<ApiResponse<CustomToolSuggestion>>(
			withBuScope(API_ENDPOINTS.CUSTOM_TOOLS.SUGGEST, scope),
			request
		);
		return response.data.data;
	},

	/** Runs the unsaved steps once on the live integration; tool errors come back as a result, not a failure. */
	async test(request: CustomToolTestRequest, scope: BuScope = {}): Promise<ToolRunResult> {
		const response = await api.post<ApiResponse<ToolRunResult>>(withBuScope(API_ENDPOINTS.CUSTOM_TOOLS.TEST, scope), request);
		return response.data.data;
	},

	/** Refused with a 409 when the slug is taken. */
	async create(payload: CustomToolPayload, scope: BuScope = {}): Promise<CustomTool> {
		const response = await api.post<ApiResponse<{ tool: CustomTool }>>(withBuScope(API_ENDPOINTS.CUSTOM_TOOLS.BASE, scope), payload);
		return response.data.data.tool;
	},

	async update(id: string, update: CustomToolUpdate, scope: BuScope = {}): Promise<CustomTool> {
		const response = await api.patch<ApiResponse<{ tool: CustomTool }>>(
			withBuScope(API_ENDPOINTS.CUSTOM_TOOLS.BY_ID(id), scope),
			update
		);
		return response.data.data.tool;
	},

	async remove(id: string): Promise<void> {
		await api.delete(API_ENDPOINTS.CUSTOM_TOOLS.BY_ID(id));
	}
};
